/**
 * B1020 (d.3) — the gift-card checkout: what the merged cart & pay page
 * (73:2) wears when the cart holds ONLY gift cards. Frames 855:268 @1440 ·
 * 855:2 (Cart) and 855:131 (Pay) @390 — sidecars
 * `redline-storefront-gift-cards-{cart,cart-390,pay}.json`.
 *
 * Wide: CHECKOUT / Your cart over two columns — the cart card (N GIFT CARDS,
 * a row per card, ADD ANOTHER) and BEFORE YOU PAY on the left, the PAY card
 * on the right (each card, Fees, Total, the wallets, YOUR NAME, the email,
 * the card, PAY seated in the card). Narrow: two steps of one page — Cart
 * (the cart card, the totals card, CONTINUE TO PAY) and Pay (the cart's
 * summary with EDIT, the pay card, PAY floating). The step is the App's,
 * so the trail above can say which one this is; CSS hides the other half.
 *
 * Nothing a gift card cannot use is drawn: no promo field (promo codes never
 * apply to a gift card), no phone (no service to update), no CLEAR CART (each
 * row has REMOVE and nothing is held), no gift-card tender (a gift card can't
 * buy a gift card). The money is the kernel's: the Total is `props.total`.
 */
import type { ReactElement } from "react";
import { Banner, Button, Input, LabelledRule, PaymentWait, SheetCard, StickyBar } from "@transact/ui";
import { formatMoney, type Money } from "../lib/money.js";
import { cartActKey, doorBusy, busyWords } from "../lib/busyFace.js";
import { GIFT_CARD_CART_NOTE, giftCardFacts, giftCardSumWords } from "../lib/giftCardCartFace.js";
import { AcknowledgeBox, OptInBox, type CartPayView } from "./cartPay.js";

type Props = Parameters<typeof CartPayView>[0];

/** The card lines' own sum — the face's figure until the kernel's total is known. */
function cardsSum(props: Props): Money | undefined {
  if (props.total !== undefined) return props.total;
  const cards = props.giftCards ?? [];
  if (cards.length === 0) return undefined;
  const currency = cards[0]!.amount.currency;
  if (cards.some((c) => c.amount.currency !== currency)) return undefined;
  return { amount: cards.reduce((n, c) => n + c.amount.amount, 0), currency };
}

const count = (n: number): string => `${n} gift ${n === 1 ? "card" : "cards"}`;

export function GiftCartPay(props: Props): ReactElement {
  const cards = props.giftCards ?? [];
  const sum = cardsSum(props);
  const zero = sum !== undefined ? { amount: 0, currency: sum.currency } : undefined;
  const charges = props.charges ?? [];
  const working = busyWords(props.cartBusy);
  const payStep = props.giftStep === "pay";
  const payWall = !props.gatewayReady
    ? (props.gatewayNotice ?? "Card payment isn’t set up on this booking page yet. Ask the seller how to pay — nothing has been charged.")
    : undefined;
  const wallets = props.wallets !== undefined && props.wallets.length > 0;
  const goStep = (s: "cart" | "pay"): void => {
    props.onGiftStep?.(s);
  };

  return (
    <div className={payStep ? "cartpay cartpay--gift cartpay--gift-paystep" : "cartpay cartpay--gift cartpay--gift-cartstep"}>
      {props.onContinue !== undefined ? (
        <div className="back">
          <Button variant="ghost" onClick={props.onContinue}>
            ‹ Keep shopping
          </Button>
        </div>
      ) : null}
      <header className="cartpay-gift-head" data-redline="gcart-head">
        <p className="cartpay-gift-kicker">Checkout</p>
        <h1 className="cartpay-gift-title">Your cart</h1>
      </header>

      <div className="cartpay-gift-main">
        <SheetCard redline="gcart-cart" className="cartpay-cart cartpay-gift-cart">
          <header className="cartpay-gift-cart-head">
            <h2 className="cartpay-label">
              <span className="cartpay-gift-narrow">Your cart</span>
              <span className="cartpay-gift-wide" data-redline="gcart-count">
                {count(cards.length)}
              </span>
            </h2>
            <span className="cartpay-gift-wide cartpay-gift-held" data-redline="gcart-held">
              Nothing is held — gift cards don’t take seats.
            </span>
            <span className="cartpay-gift-narrow cartpay-gift-tally" aria-hidden="true">
              {count(cards.length)}
            </span>
          </header>
          {working !== undefined ? (
            <p className="cartpay-working" role="status">
              {working}
            </p>
          ) : null}
          <ul className="cartpay-gift-rows">
            {cards.map((c) => {
              const [to, when] = giftCardFacts(c);
              return (
                <li key={c.lineId} className="cartpay-gift-row" data-redline="gcart-row">
                  <span className="cartpay-gift-swatch" aria-hidden="true" />
                  <div className="cartpay-gift-body">
                    <p className="cartpay-gift-line">
                      <span className="cartpay-gift-name">Gift card</span>
                      <span className="cartpay-gift-amount">{formatMoney(c.amount)}</span>
                    </p>
                    <p className="cartpay-gift-fact">{to}</p>
                    <p className="cartpay-gift-fact">{when}</p>
                    <div className="cartpay-gift-doors" data-redline="gcart-row-doors">
                      {props.giftCardEditHref !== undefined ? (
                        /* dead-ok: the href navigates — Button draws a real <a> for it, the press needs no handler */
                        <Button variant="textlink" href={props.giftCardEditHref(c.lineId)} ariaLabel={`Edit gift card for ${c.to}`} onClick={() => undefined}>
                          Edit
                        </Button>
                      ) : null}
                      {props.onRemove !== undefined ? (
                        <Button
                          variant="textlink"
                          ariaLabel={`Remove gift card for ${c.to}`}
                          busy={doorBusy(props.cartBusy, cartActKey("cart.remove", props.cartId, c.lineId))}
                          onClick={() => props.onRemove?.(c.lineId)}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="cartpay-gift-addrow">
            {props.giftCardsHref !== undefined ? (
              /* dead-ok: the href navigates — Button draws a real <a> for it */
              <Button variant="neutral" href={props.giftCardsHref} data={{ redline: "gcart-add" }} onClick={() => undefined}>
                + Add another gift card
              </Button>
            ) : null}
            <p className="cartpay-gift-wide cartpay-gift-aside" data-redline="gcart-tickets">
              Buying tickets too? They sit in this cart and are paid together — the gift cards can pay for the tickets, never for the new gift cards.
            </p>
          </div>
          <p className="cartpay-gift-narrow cartpay-gift-aside">
            Nothing is held — gift cards don’t take seats. Each card goes to its own person; a copy of each comes to you.
          </p>
        </SheetCard>

        <SheetCard redline="gcart-before" className="cartpay-gift-wide cartpay-gift-before">
          <h2 className="cartpay-label">Before you pay</h2>
          <p className="cartpay-gift-aside">Each card is emailed to its own person, with a copy to you. A scheduled card can be changed or canceled from Your orders until it sends.</p>
          <p className="cartpay-gift-aside">Never expires, no fees. Promo codes and other gift cards don’t apply to gift cards — the full amount is what’s loaded.</p>
          <p className="cartpay-gift-aside">An unused card can be refunded to your card from Your orders; once any of it is spent it can’t.</p>
        </SheetCard>

        {/* 855:2 — the phone's Cart step: the totals, then CONTINUE TO PAY. */}
        <SheetCard redline="gcart-cartsum" className="cartpay-gift-narrow cartpay-gift-cartsum">
          <p className="cartpay-gift-sumline">
            <span>{count(cards.length)}</span>
            {sum !== undefined ? <span>{formatMoney(sum)}</span> : null}
          </p>
          <FeeLines charges={charges} zero={zero} />
          <p className="cartpay-gift-aside">{GIFT_CARD_CART_NOTE}</p>
          <p className="cartpay-gift-total">
            <span>Total</span>
            {sum !== undefined ? <span className="cartpay-gift-total-figure">{formatMoney(sum)}</span> : null}
          </p>
        </SheetCard>
        <p className="cartpay-gift-narrow cartpay-gift-aside cartpay-gift-tickets">
          Buying tickets too? They sit in the same cart and are paid together — your gift cards can pay for the tickets, never for the new gift cards.
        </p>
        <div className="cartpay-gift-narrow">
          <StickyBar redline="gcart-continue" summary="Secure checkout · Powered by transact">
            <Button size="lg" onClick={() => goStep("pay")}>
              {sum !== undefined ? `Continue to pay · ${formatMoney(sum)}` : "Continue to pay"}
            </Button>
          </StickyBar>
        </div>
      </div>

      <div className="cartpay-gift-side">
        <SheetCard redline="gcart-sum" className="cartpay-gift-sum">
          <header className="cartpay-gift-cart-head">
            <h2 className="cartpay-label">
              <span className="cartpay-gift-wide">Pay</span>
              <span className="cartpay-gift-narrow">Your cart</span>
            </h2>
            {props.onGiftStep !== undefined ? (
              <span className="cartpay-gift-narrow">
                <Button variant="textlink" data={{ redline: "gcart-sum-edit" }} ariaLabel="Edit your cart" onClick={() => goStep("cart")}>
                  Edit
                </Button>
              </span>
            ) : null}
          </header>
          {cards.map((c) => (
            <p key={c.lineId} className="cartpay-gift-sumline">
              <span>{giftCardSumWords(c)}</span>
              <span>{formatMoney(c.amount)}</span>
            </p>
          ))}
          <div className="cartpay-gift-wide">
            <FeeLines charges={charges} zero={zero} />
          </div>
          <p className="cartpay-gift-total" data-redline="gcart-total">
            <span>Total</span>
            {sum !== undefined ? <span className="cartpay-gift-total-figure">{formatMoney(sum)}</span> : null}
          </p>
        </SheetCard>

        <SheetCard redline="gcart-pay" className="cartpay-pay cartpay-gift-pay">
          <h2 className="cartpay-label cartpay-gift-narrow">Pay</h2>
          {props.authorized === true ? (
            <PaymentWait redline="pay-wait" />
          ) : (
            <>
              {/* B511 — the wallets' own native buttons mount here; one press = the order. */}
              <div id="express-checkout-element" className="cartpay-wallets" data-redline="gcart-wallets" hidden={!wallets} />
              {wallets ? <LabelledRule label="or pay by card" /> : null}
              {/* enter-ok: the only door here is the money verb (PAY) — Enter in a name field must never spend */}
              <Input
                id="buyer-name" autoComplete="name" required
                label="Your name"
                value={props.buyerName}
                placeholder="Priya Natarajan"
                onChange={props.onBuyerName}
                error={props.nameRefusal}
                redline="gcart-name"
              />
              <Input
                id="buyer-email" autoComplete="email" required
                label={props.emailLabel ?? "Email for your receipt"}
                type="email"
                value={props.buyerEmail}
                placeholder="you@example.com"
                onChange={props.onBuyerEmail}
                error={props.emailRefusal}
                redline="gcart-email"
              />
              <OptInBox {...props} />
              <AcknowledgeBox {...props} />
              {/* Stripe's deferred-intent element (§7 step 4: the card fields render inside OUR origin). */}
              <div id="payment-element" data-redline="gcart-card" />
              {payWall !== undefined ? (
                <Banner tone="info" id="pay-gate">
                  {payWall}
                </Banner>
              ) : null}
              <p className="cartpay-gift-narrow cartpay-gift-aside cartpay-gift-trust">
                {props.gatewayReady ? "Card details go straight to the payment provider — never through us. " : ""}
                Gift cards aren’t a tender here: a gift card can’t buy a gift card.
              </p>
            </>
          )}
        </SheetCard>
        {/* The door sits OUTSIDE the glass card: the card's frost is the containing
            block of a fixed child, so inside it the phone's bar could not float. */}
        {props.authorized === true ? null : (
          <StickyBar redline="gcart-paybar" seatedWhenWide summary={<span className="cartpay-gift-narrow">Secure checkout · Powered by transact</span>}>
            <Button
              size="lg"
              disabled={payWall !== undefined}
              busy={props.paying}
              {...(payWall !== undefined ? { describedBy: "pay-gate" } : {})}
              onClick={props.onPay}
              data={{ redline: "gcart-pay-button" }}
            >
              {sum !== undefined ? `Pay ${formatMoney(sum)}` : "Pay"}
            </Button>
          </StickyBar>
        )}
        <p className="cartpay-gift-wide cartpay-gift-aside cartpay-gift-trust" data-redline="gcart-trust">
          {props.gatewayReady ? "Card details go straight to the payment provider — never through us. " : ""}Secure checkout · Powered by transact
        </p>
        <p className="cartpay-gift-narrow cartpay-gift-aside cartpay-gift-after">
          After payment each card is emailed on the day its row says; a scheduled one can be changed or canceled from Your orders until then.
        </p>
      </div>
    </div>
  );
}

/** Each fee the buyer pays, by name — or "Fees $0.00" when there are none (855:268 states it). */
function FeeLines(props: { readonly charges: readonly { readonly key: string; readonly label: string; readonly amount: Money }[]; readonly zero: Money | undefined }): ReactElement {
  if (props.charges.length > 0) {
    return (
      <>
        {props.charges.map((c) => (
          <p key={c.key} className="cartpay-gift-sumline">
            <span>{c.label}</span>
            <span>{formatMoney(c.amount)}</span>
          </p>
        ))}
      </>
    );
  }
  return (
    <p className="cartpay-gift-sumline">
      <span>Fees</span>
      {props.zero !== undefined ? <span>{formatMoney(props.zero)}</span> : null}
    </p>
  );
}
